import { useEffect, useRef, useState } from "react";
import {
  VERTEX360_COPILOT_TYPEWRITER_CHARS_PER_TICK,
  VERTEX360_COPILOT_TYPEWRITER_INTERVAL_MS,
} from "../constants/vertex360CopilotConstants";

/**
 * Smoothly reveals streamed assistant text (typewriter chase).
 * While active, display lags slightly behind target for a seamless feel.
 * When inactive, snaps to the full target immediately.
 */
export const useTypewriterReveal = (targetContent, isActive) => {
  const [displayContent, setDisplayContent] = useState(() =>
    isActive ? "" : String(targetContent || "")
  );
  const targetRef = useRef("");
  const timerRef = useRef(null);

  useEffect(() => {
    targetRef.current = String(targetContent || "");
  }, [targetContent]);

  useEffect(() => {
    if (!isActive) {
      setDisplayContent(String(targetContent || ""));
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }

    const target = targetRef.current;

    if (displayContent.length >= target.length) {
      if (displayContent !== target) {
        setDisplayContent(target);
      }
      return undefined;
    }

    const tick = () => {
      setDisplayContent((previous) => {
        const nextTarget = targetRef.current;
        if (previous.length >= nextTarget.length) {
          return previous;
        }
        return nextTarget.slice(
          0,
          previous.length + VERTEX360_COPILOT_TYPEWRITER_CHARS_PER_TICK
        );
      });
    };

    timerRef.current = window.setTimeout(tick, VERTEX360_COPILOT_TYPEWRITER_INTERVAL_MS);

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [displayContent, isActive, targetContent]);

  return displayContent;
};
